import {
  Typography,
  Alert,
  FormControl,
  TextField,
  Button,
  Autocomplete,
  LinearProgress,
  Dialog,
  IconButton,
  DialogContent,
  FormHelperText,
  Switch,
  MenuItem,
  Select,
  InputLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Box,
  OutlinedInput,
  Chip,
  Tooltip,
} from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import {
  CreateEmployeeFormProps,
  EmployeeType,
} from './create-employee-form.interfaces';
import { EmployeeInput, emptyEmployee } from './create-employee-form.utils';
import React from 'react';
import MuiPhoneNumber from 'mui-phone-number';
import ConfirmAction from '../../../common/confirm-action/confirm-action';
import axios, { AxiosResponse } from 'axios';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import AutocompleteWithThrottle from '../../../common/autocomplete-with-throttle/autocomplete-with-throttle';
import { BranchType } from '../../../../interfaces/delivery-company/branches';
import CreateBranchForm from '../../branches/create-branch-form/create-branch-form';
import ClearIcon from '@mui/icons-material/Clear';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { ErrorsFlattenner } from '../../../common/errors-flattenner/errors-flattenner';
import { MuiTelInput } from 'mui-tel-input';
import { isMobile, isTablet } from 'react-device-detect';
import CustomDialog from '../../../common/custom-dialog/custom-dialog';
import { IRole, UserRole } from '../../../../interfaces/common/roles';

// ICONS
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { MonetizationOn } from '@mui/icons-material';
import PasswordField from '../../../common/password-field/password-field';
import useRoles from '../../../../hooks/roles';
import { DataResources, ResourceActionType, user_has_data_model_access } from '../../../../utils/permissions';
import { useSnackBar } from '../../../../contexts/snackbar';
import { BranchSelector } from '../../../common/selectors/branch-selector';

function CreateEmployeeForm({ defaultEmployeeId }: CreateEmployeeFormProps) {
  const [employeeData, setEmployeeData] = useState<EmployeeInput>();
  const [isLoading, setIsLoading] = useState(false);

  const { roles, isLoading: rolesLoading } = useRoles()

  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [confirmDeleteEmployee, setConfirmDeleteEmployee] =
    useState<boolean>(false);
  const [response, setResponse] = useState<AxiosResponse>();
  const [message, setMessage] = useState<string>('');
  const [creatingNewBranchOpen, setCreatingNewBranchOpen] =
    useState<boolean>(false);
  const [enableModifyPassword, setEnableModifyPassword] =
    useState<boolean>(false);
  const [permissionInput, setPermissionInput] = useState<string>('');
  const {showMessage} = useSnackBar();

  const { user, config } = useContext(AuthContext);

  React.useEffect(() => {
    const getAndSetEmployee = async () => {
      if (defaultEmployeeId !== undefined) {
        setIsLoading(true);
        try {
          const response = await axios.get<EmployeeInput>(
            `${API_ENDPOINT}/api/v1/employee/${defaultEmployeeId}`,
            config
          );
          setResponse(response);
          setEmployeeData(response.data);
        } catch (e) {
          setEmployeeData(undefined);
          if (axios.isAxiosError(e)) {
            setMessage('Failed to load employee  data: ' + e.message);
          } else {
            setMessage('Failed to load employee  data');
          }
          console.log(e);
        }
        setIsLoading(false);
      } else {
        setEmployeeData(emptyEmployee);
      }
    };
    getAndSetEmployee();
  }, []);

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (user === null) return;
    if (employeeData) {
      setIsLoading(true);
      setResponse(undefined);
      setLoadingMessage('Saving Employee...');
      const requestData = {
        name: employeeData.name,
        phone_number: employeeData.phone_number,
        branches: employeeData.branches,
        organisation: user?.organisation,
        is_admin: employeeData.is_admin,
        password: employeeData.password,
        roles: employeeData.roles.map(r => r.id)
      };

      try {

        let response;

        if (defaultEmployeeId === undefined) {
          response = await axios.post(
            `${API_ENDPOINT}/api/v1/employees/`,
            requestData,
            config
          );
        } else {
          response = await axios.patch(
            `${API_ENDPOINT}/api/v1/employee/${employeeData.id}/`,
            requestData,
            config
          );
        }
        setResponse(response);

        if (response.status >= 200 && response.status < 300) {
          setMessage('Employee saved successfully');
        }
      } catch (e: any) {
        if (axios.isAxiosError(e)) {
          setResponse(e.response);
          setMessage('Failed to save employee: ' + e.message);
        } else {
          setMessage('Failed to save employee');
        }
        console.log(e);
      }
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  const deleteEmployee = async () => {
    if (employeeData) {
      setIsLoading(true);
      setResponse(undefined);
      try {
        const response = await axios.delete(
          `${API_ENDPOINT}/v1/org/employee/${employeeData.id}/`,
          config
        );
        setResponse(response);
        if (response.status === 204) {
          setEmployeeData(undefined);
          setMessage('Employee deleted successfully');
        }
      } catch (e) {
        if (axios.isAxiosError(e)) {
          setMessage('Failed to delete employee: ' + e.message);
        } else {
          setMessage('Failed to delete employee');
        }
        console.log(e);
      }

      setIsLoading(false);
    }
  };


  return (
    <>
      <Typography
        variant="h3"
        component="h3"
        sx={{ textAlign: 'center', mt: 3, mb: 3 }}
      >
        {defaultEmployeeId === undefined ? 'Create Employee' : 'Edit Employee'}
      </Typography>
      {isLoading ? (
        <>
          {loadingMessage}
          <br />
          <LinearProgress />
        </>
      ) : (
        <>
          {message != '' && (
            <Alert
              sx={{ maxWidth: 500, margin: "auto", mb: 2 }}
              severity={
                response && response.status >= 200 && response.status < 300
                  ? 'success'
                  : 'error'
              }
            >
              <>
                {message}

                {response && response.status >= 300 && (
                  <ErrorsFlattenner rawErrors={response.data} />
                )}
              </>
            </Alert>
          )}

          {employeeData && (
            <Box display={"flex"} justifyContent={"center"} width={"100%"}>


              <form onSubmit={handleSubmit}>

                <BranchSelector
                  multiSelect={true}
                  selectedBranches={employeeData.branches}
                  onSelectMultiple={(branches) => setEmployeeData({...employeeData, branches: branches})}
                  selectFirstOnLoad={!defaultEmployeeId}
                  />


                <FormControl fullWidth sx={{ mt: 2, maxWidth: 500 }}>
                  <TextField
                    autoComplete="off"
                    label="Employee Name"
                    type="text"
                    variant="outlined"
                    required
                    value={employeeData.name}
                    onChange={(e) =>
                      setEmployeeData({ ...employeeData, name: e.target.value })
                    }
                  />
                </FormControl>

                <br />
                <br />

                <FormControl fullWidth sx={{ mb: 1, maxWidth: 500 }}>
                  <MuiTelInput
                    autoComplete="off"
                    forceCallingCode
                    label="Phone Number"
                    disabled={isLoading}
                    defaultCountry={'LB'}
                    variant="outlined"
                    required
                    value={employeeData.phone_number}
                    onChange={(newPhone) => {
                      if (typeof newPhone === 'string') {
                        setEmployeeData({
                          ...employeeData,
                          phone_number: newPhone,
                        });
                      }
                    }}
                  />
                </FormControl>

                <Box sx={{ mt: 1, maxWidth: 500,  }} display={"flex"} alignItems={"center"} justifyContent={"flex-start"} padding={0}>
                  
                  {defaultEmployeeId && <Switch
                    onChange={() =>
                      setEnableModifyPassword(!enableModifyPassword)
                    }
                    checked={enableModifyPassword}
                  />} 
                    
                  <Box>
                    {!enableModifyPassword && defaultEmployeeId && <>Modify employee password</>}
                    {(!defaultEmployeeId || enableModifyPassword) && (
                      <Box width={"100%"}>
                        <PasswordField
                          label="Employee Password"
                          placeholder="Password"
                          value={employeeData.password ?? ''}
                          onPasswordChange={(value) =>
                            setEmployeeData({
                              ...employeeData,
                              password: value,
                            })
                          }
                          variant="outlined"
                        />
                      </Box>
                    )}

                  </Box>
                    
                </Box>

                <Box sx={{ mt: 1, maxWidth: 500,  }} display={"flex"} alignItems={"center"} justifyContent={"flex-start"} padding={0}>
                  
                      <Switch
                        checked={employeeData.is_admin}
                        onChange={(e) => {
                          setEmployeeData((prev) => ({
                            ...prev!,
                            is_admin: !prev?.is_admin,
                            roles: []
                          }));
                        }}
                      />
                   
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <AdminPanelSettingsIcon color="primary" />
                        <Typography>Grant Admin Privileges</Typography>
                      </Box>
                    
                  
                </Box>

                {!employeeData.is_admin && <FormControl fullWidth sx={{ mt: 2, mb: 2, maxWidth: 500 }}>
                  <Autocomplete
                    disablePortal
                    multiple
                    loading={rolesLoading}
                    value={employeeData.roles}
                    onChange={(
                      event: React.SyntheticEvent<Element, Event>,
                      newValue: IRole[]
                    ) => {
                      setEmployeeData({
                        ...employeeData,
                        roles: newValue,
                      });
                    }}
                    inputValue={permissionInput}
                    onInputChange={(
                      event: any,
                      newInputValue: string,
                      reason
                    ) => {
                      if (reason === 'input')
                        setPermissionInput(newInputValue);
                    }}
                    id="controllable-states-demo"
                    options={roles}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderOption={(params, option) => (
                      <li {...params} key={option.id}>
                        <Box>
                          <Typography>{option.name}</Typography>
                          <Typography variant='body2'>{option.description}</Typography>
                        </Box>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} label="Permission Groups" />
                    )}
                  />

                  <FormHelperText>
                    (*) If a user is assigned to multiple permission groups at the same time, an action will be permitted of at least one permission group permits it.
                  </FormHelperText>
                </FormControl>
                }

                <Box sx={{ display: 'flex', gap: 2, mt:1 }}>
                  <Button
                    size={(isMobile && !isTablet) ? 'small' : 'medium'}
                    type="submit"
                    variant="contained"
                    disabled={isLoading || (defaultEmployeeId != undefined && !user_has_data_model_access(user, DataResources.EMPLOYEE, ResourceActionType.UPDATE))}
                  >
                    Save Employee
                  </Button>

                  {employeeData.id !== '' && (
                    <Button
                      size={(isMobile && !isTablet) ? 'small' : 'medium'}
                      onClick={() => {
                        setConfirmDeleteEmployee(true);
                      }}
                      variant="outlined"
                      color="error"
                      disabled={isLoading || (defaultEmployeeId != undefined && !user_has_data_model_access(user, DataResources.EMPLOYEE, ResourceActionType.DELETE))}
                    >
                      Delete Employee
                    </Button>
                  )}
                </Box>
              </form>
            </Box>
          )}
        </>
      )}

      <ConfirmAction
        message={'Are you sure you want to delete this employee?'}
        action={deleteEmployee}
        isOpen={confirmDeleteEmployee}
        handleClose={() => setConfirmDeleteEmployee(false)}
      >
        <div> Attention: This action can not be undone</div>
      </ConfirmAction>

      <CustomDialog
        fullWidth
        open={creatingNewBranchOpen}
        fullScreen={(isMobile && !isTablet)}
        onClose={() => {
          setCreatingNewBranchOpen(false);
        }}
      >
        <div
          style={{
            textAlign: 'right',
            width: '100%',
          }}
        >
          <IconButton onClick={() => setCreatingNewBranchOpen(false)}>
            <ClearIcon color="primary" fontSize="large" />
          </IconButton>
        </div>

        <DialogContent>
          <CreateBranchForm />
        </DialogContent>
      </CustomDialog>
    </>
  );
}

export default CreateEmployeeForm;
